import { useEffect, useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HardHat, ShieldCheck, FileCheck2, Building2, MapPin, Check, Loader2, AlertTriangle, LayoutDashboard, Users, FileText, Bell, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLoading } from "@/components/shared";
import api from "@/lib/api";

const MODULE_KEY = "SUBCONTRACTOR_COMPLIANCE";

export default function Subcontractors() {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/employer/module/${MODULE_KEY}`).then((r) => setState(r.data)).catch(() => setState({ access: "none" })); }, []);
  const activate = async () => {
    setBusy(true);
    try { const { data } = await api.post("/employer/module/checkout", { origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch { setBusy(false); }
  };
  if (state === null) return <PageLoading />;
  if (state.access === "none") return <Landing t={t} onActivate={activate} busy={busy} />;

  const readOnly = state.access === "read_only";
  const tabs = [
    { to: "/app/employer/subcontractors", label: t("sub.tOverview"), icon: LayoutDashboard, end: true },
    { to: "/app/employer/subcontractors/companies", label: t("sub.tCompanies"), icon: Building2 },
    { to: "/app/employer/subcontractors/workers", label: t("sub.tWorkers"), icon: Users },
    { to: "/app/employer/subcontractors/documents", label: t("sub.tDocuments"), icon: FileText },
    { to: "/app/employer/subcontractors/contracts", label: t("sc.tContracts"), icon: FileCheck2 },
    { to: "/app/employer/subcontractors/compliance", label: t("sub.tCompliance"), icon: ShieldCheck },
    { to: "/app/employer/subcontractors/alerts", label: t("sub.tAlerts"), icon: Bell },
    { to: "/app/employer/subcontractors/reports", label: t("sub.tReports"), icon: BarChart3 },
  ];
  const soon = [];

  return (
    <div data-testid="subcontractor-module">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-cyan/10 text-cyan-dark"><HardHat className="h-5 w-5" /></span>
        <h1 className="font-heading text-2xl font-extrabold text-navy">{t("sub.activeTitle")}</h1>
        <span className="ml-1 inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-600"><Check className="h-3 w-3" /> {t("sub.active")}</span>
      </div>
      {state.state === "TRIAL_ACTIVE" && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-cyan/30 bg-cyan/5 px-4 py-3" data-testid="sub-trial-banner">
          <span className="text-sm text-navy">{t("sc.trialBanner")}</span>
          <Link to="/app/employer/billing" className="text-sm font-semibold text-cyan-dark hover:underline" data-testid="sub-trial-plans">{t("sc.viewPlans")}</Link>
        </div>
      )}
      {readOnly && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="sub-readonly-banner">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div><div className="font-semibold text-amber-800">{t("sub.readOnlyTitle")}</div><p className="text-sm text-amber-700">{t("sub.readOnlyDesc")}</p></div>
        </div>
      )}
      <div className="mb-6 flex flex-wrap gap-0.5 overflow-x-auto border-b border-slate-200">
        {tabs.map((tb) => (
          <NavLink key={tb.to} to={tb.to} end={tb.end} data-testid={`subtab-${tb.to.split("/").pop() || "overview"}`}
            className={({ isActive }) => `flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? "border-cyan text-cyan-dark" : "border-transparent text-slate-500 hover:text-navy"}`}>
            <tb.icon className="h-4 w-4" /> {tb.label}
          </NavLink>
        ))}
        {soon.map((s, i) => (
          <span key={i} className="flex cursor-not-allowed items-center gap-2 whitespace-nowrap border-b-2 border-transparent px-4 py-2.5 text-sm font-medium text-slate-300">
            <s.icon className="h-4 w-4" /> {s.label}<span className="rounded bg-slate-100 px-1 py-0.5 text-[8px] font-bold uppercase text-slate-400">{t("sub.soon")}</span>
          </span>
        ))}
      </div>
      <Outlet context={{ readOnly }} />
    </div>
  );
}

function Landing({ t, onActivate, busy }) {
  const features = [
    { icon: Building2, title: t("sub.f1t"), desc: t("sub.f1d") },
    { icon: FileCheck2, title: t("sub.f2t"), desc: t("sub.f2d") },
    { icon: MapPin, title: t("sub.f3t"), desc: t("sub.f3d") },
    { icon: ShieldCheck, title: t("sub.f4t"), desc: t("sub.f4d") },
  ];
  return (
    <div data-testid="subcontractor-landing" className="mx-auto max-w-5xl">
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-navy via-navy to-[#0d2440] p-8 text-white sm:p-12">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-cyan/20 px-3 py-1 text-xs font-bold uppercase tracking-wider text-cyan"><HardHat className="h-3.5 w-3.5" /> {t("sub.pro")}</span>
        <h1 className="mt-5 font-heading text-4xl font-extrabold leading-tight sm:text-5xl">{t("sub.landingTitle")}</h1>
        <p className="mt-4 max-w-2xl text-base text-slate-300">{t("sub.landingDesc")}</p>
        <div className="mt-8 flex flex-wrap items-end gap-6">
          <div>
            <div className="flex items-end gap-1"><span className="font-heading text-5xl font-extrabold">€2.000</span><span className="mb-1.5 text-sm text-slate-300">{t("sub.priceUnit")}</span></div>
            <p className="mt-1 text-xs text-slate-400">{t("sub.priceNote")}</p>
          </div>
          <Button onClick={onActivate} disabled={busy} data-testid="activate-module-btn" className="h-12 rounded-full bg-cyan px-8 text-base font-bold text-white hover:bg-cyan-dark">
            {busy ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> {t("sub.activating")}</> : t("sub.activate")}
          </Button>
        </div>
      </div>
      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        {features.map((f, i) => (
          <div key={i} className="card-soft flex gap-4 p-6" data-testid={`sub-feature-${i}`}>
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cyan/10 text-cyan-dark"><f.icon className="h-5 w-5" /></div>
            <div><h3 className="font-heading font-bold text-navy">{f.title}</h3><p className="mt-1 text-sm text-slate-500">{f.desc}</p></div>
          </div>
        ))}
      </div>
      <p className="mt-6 text-center text-xs text-slate-400">{t("sub.legalNote")}</p>
    </div>
  );
}
